import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton, SkeletonText } from '@/components/Skeleton';
import { findSection } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import { LEGAL_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

export type LegalSlug = 'privacy' | 'terms' | 'cookies';

// Shared renderer for the three legal pages — each is a single rich_text CMS
// section (title + body_html) read from the typed sections document, with an
// i18n fallback when the page has no content. Routed per slug from router.tsx.
export function LegalPage({ slug }: { slug: LegalSlug }) {
  const { t, i18n } = useTranslation();
  useSeoHead(LEGAL_META[slug]);
  const [content, setContent] = useState<{ title: string; html: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const locale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  useEffect(() => {
    setLoading(true);
    getCmsDocument(slug, locale)
      .then((doc) => {
        const rt = findSection(doc, 'rich_text');
        setContent(rt ? { title: rt.title, html: rt.html } : null);
      })
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [slug, locale]);

  if (loading) {
    return (
      <div className="public">
        <div className="legal-shell">
          <article className="legal-content" aria-busy="true">
            <Skeleton width={120} height={12} />
            <div style={{ height: 16 }} />
            <Skeleton width="70%" height={36} />
            <div style={{ height: 12 }} />
            <Skeleton width={160} height={12} />
            <div style={{ height: 28 }} />
            <SkeletonText lines={8} lastWidth="55%" gap={12} />
          </article>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="public">
        <div className="legal-shell">
          <article className="legal-content">
            <div className="legal-content__kicker">{t('legal.kicker')}</div>
            <h1 className="legal-content__title">{t('legal.title')}</h1>
            <p style={{ color: 'var(--c-muted)' }}>Content not available.</p>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="public">
      <div className="legal-shell">
        <article className="legal-content">
          <div className="legal-content__kicker">{t('legal.kicker')}</div>
          <h1 className="legal-content__title">{content.title}</h1>
          <div className="legal-content__updated">{t('legal.updated')}</div>
          <div className="legal-section" dangerouslySetInnerHTML={{ __html: content.html }} />
        </article>
      </div>
    </div>
  );
}
